<template>
  <transition name="modal">
    <div
      class="xmodal-wrapper"
      id="opal-modal"
      v-if="modalActive"
      @click.self="allowClose && closeModal"
      :class="[
        `text-align-${store.state.modal.textAlign}`,
        { 'xform-changes-modal': store.state.modal.type === ModalTypes.FormChanges },
      ]"
    >
      <div class="xmodal-inner" :data-fullscreen="store.state.modal.fullscreen">
        <div
          v-if="allowClose"
          class="xmodal-close-icon"
          @click="closeModal"
          :disabled="store.state.modal.closeBtnDisabled"
        >
          <font-awesome-icon icon="fa-solid fa-x" size="xl" />
        </div>

        <div class="xmodal">
          <h2
            v-if="store.state.modal.title"
            class="xmodal-title"
            :class="store.state.modal.titleClassList || []"
          >
            {{ store.state.modal.title }}
          </h2>
          <p v-if="store.state.modal.description" class="xmodal-description">
            {{ store.state.modal.description }}
          </p>

          <div
            v-if="store.state.modal.html"
            v-html="store.state.modal.html"
            class="xmodal-html"
          ></div>

          <div v-if="store.state.modal.changes && store.state.modal.changes.length > 0">
            <table class="xmodal-changes">
              <thead>
                <th></th>
                <th id="old">Previous</th>
                <th id="new">New</th>
              </thead>
              <tbody>
                <tr v-for="(change, index) in store.state.modal.changes" :key="index">
                  <td>{{ change.label }}</td>
                  <td>{{ change.before }}</td>
                  <td>{{ change.after }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="store.state.modal.buttons.length > 0"
            class="xmodal-buttons"
            :class="[
              {
                'center-buttons': store.state.modal.centerButtons,
              },
            ]"
            :position="store.state.modal.buttonsPosition || ''"
          >
            <button
              v-for="(button, index) in store.state.modal.buttons"
              :key="index"
              :class="[
                { loading: button?.loading },
                { 'branded-button': isDarkBrand && !button.isCancel },
                button.classStyle,
              ]"
              :style="
                !button.isCancel ? { backgroundColor: store.state.lenderData.primaryColor } : {}
              "
              @click="button.onClick ? button.onClick() : closeModal"
              :disabled="button?.loading || false"
            >
              <span class="button-text">{{ button.title }}</span>
            </button>
          </div>
          <div v-else class="xmodal-buttons" :position="store.state.modal.buttonsPosition || ''">
            <button class="primary-btn" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useStore } from "vuex";
import useHtmlUtilities from "@/use/htmlUtilities";
import { ModalTypes } from "@/models/modal";

const store = useStore();
const modalActive = ref<boolean>(false);

const modalState = computed(() => store.state.modal);

const allowClose = computed<boolean>(() => store.state.modal.allowClose ?? true);

const { isColorDark } = useHtmlUtilities();

const isDarkBrand = computed(() => isColorDark(store.state.lenderData?.primaryColor || "#000000"));

watch(
  () => store.state.modal.active,
  value => {
    modalActive.value = value;
  },
  { deep: true },
);

function closeModal() {
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "visible";
  }
  store.dispatch("modal/setCountDownModal", 0);
  store.dispatch("modal/closeModal");
}

defineExpose({
  modalState,
});
</script>

<style scoped src="@/styles/modal/xmodal-changes.css"></style>

<style scoped>
div.xmodal-wrapper {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

div.xmodal-inner {
  width: 600px;
  background: white;
  margin: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0px 0px 10px #c7c7c7;
  border-radius: 4px;
}

.xmodal-close-icon {
  position: absolute;
  cursor: pointer;
  top: 16px;
  right: 16px;
  height: 16px;
  width: 16px;
}

div.xmodal {
  padding: 30px;
  overflow: hidden;
  max-height: 96vh;
  text-align: center;
}

div.xmodal.text-align-center {
  text-align: center;
}

div.xmodal.text-align-left {
  text-align: left;
}

div.xmodal.text-align-right {
  text-align: right;
}

div.xmodal-html {
  max-height: 40vh;
  overflow: hidden;
  overflow-y: auto;
}

div.xmodal h2 {
  padding: 0;
}

div.xmodal-close {
  cursor: pointer;
  float: right;
  font-size: 20px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

div.xmodal-inner[data-fullscreen="true"] {
  width: 96%;
  height: 90%;
}

div.xmodal-inner[data-fullscreen="true"] div.xmodal {
  height: 100%;
}

div.xmodal-inner[data-fullscreen="true"] div.xmodal-html {
  position: absolute;
  bottom: 35px;
  top: 100px;
  right: 35px;
  left: 35px;
  max-height: none;
}

div.xmodal-inner[data-fullscreen="true"] div.xmodal-html iframe {
  width: 100%;
  height: 100%;
}

table.xmodal-changes {
  width: 100%;
}

table.xmodal-changes td {
  color: #000000;
  font-size: 14px;
  font-weight: normal;
  font-family: "Montserrat", arial, sans-serif;
  padding: 10px 10px 10px 0;
  border-bottom: 1px solid #dddddd;
}

table.xmodal-changes tr:last-of-type td {
  border-bottom: 0;
}

table.xmodal-changes td.xbefore {
  padding-right: 20px;
  color: #222222;
  text-transform: uppercase;
  font-size: 12px;
  font-family: "Montserrat";
  font-weight: 600;
}

table.xmodal-changes td.xafter {
  font-weight: bold;
}

div.xmodal-buttons {
  padding-top: 15px;
  text-align: right;
}

div.xmodal-buttons {
  display: flex;
  gap: 8px;
}

div.xmodal-buttons[position="right"] {
  justify-content: flex-end;
}
div.xmodal-buttons[position="center"] {
  justify-content: center;
}
div.xmodal-buttons[position="left"] {
  justify-content: flex-start;
}

#opal-modal div.xmodal-buttons button {
  position: relative;
  cursor: pointer;
  border: 0;
  color: white;
  padding: 0 10px;
  line-height: 35px;
  border-radius: 3px;
  font-weight: bold;
  font-size: 14px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  background-color: var(--theme-color);
}

.xmodal-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 42px;
  text-align: center;
}

.xmodal-description {
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 17.18px;
  letter-spacing: 0;
  text-align: center;
}

.center-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
}

.xmodal-buttons:is(.center-buttons) {
  display: flex;
  gap: 8px;
}

.btn-gray {
  background: #e2e4e9 !important;
  color: black !important;
}

.branded-button {
  background-color: var(--theme-color, #000);
  color: #fff !important;
}

/* Style for form changes */
.xform-changes-modal .xmodal-title {
  text-align: left !important;
  color: #000;
  font-family: "Montserrat";
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 17.175px; /* 71.563% */
}

.xform-changes-modal .xmodal-description {
  text-align: left !important;
  color: #000;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

@media (max-width: 992px) {
  div.xmodal-inner {
    width: 96%;
  }
}

@media (max-width: 480px) {
  div.xmodal-inner {
    width: 96%;
  }
}
</style>

<style src="@/styles/button/loading.css" scoped></style>
